import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { UserService } from 'src/app/services/user.service';
import { UserVM } from 'src/app/models/user/user-vm.model';
import { Observable, catchError, delay, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { FieldEditability, PreviewVariant, SignatureProfileStatus, TemplateVariant } from '../enums/signature.enum';
import { ISignatureProfile, IUpdateProfileRequest } from '../models/ISignatureProfile.interface';
import { ISaveTemplateRequest, ISignatureTemplate } from '../models/ISignatureTemplate.interface';
import {
  applyFixtureIdentity, FixtureIdentity, FIXTURE_ENTITY_BRANDING, FIXTURE_PROFILE_COUNTS,
  seedProfiles, seedTemplates,
} from './signature-fixtures.data';
import { renderSignature } from './signature-fixture.renderer';

/**
 * Answers the signatures endpoints out of memory, so the module can be demonstrated without a
 * Corporate instance to hand. Off unless `environment.useSignatureFixtures` is true — which is
 * `environment.dev.ts` only; every production environment file sets it false.
 *
 * **Why an interceptor and not a flag inside the services.** `SignatureService` and
 * `SignatureAdminService` stay exactly as they will ship: no `if (fixtures)` branch, no injected
 * fake, no second code path that production never runs and nobody remembers to keep in step. The
 * screens issue real HTTP requests and this answers them, so what is being tested is the same call
 * stack that will hit the real API. Deleting this directory and the two provider lines removes it
 * completely.
 *
 * It sits in `withInterceptors([...])`, which runs ahead of the DI interceptors, so a fixtured
 * request short-circuits before `MsalCustomerInterceptor` tries to acquire a token for a scope that
 * does not exist yet.
 */

const LATENCY_MS = 260;

/** Mutable session state. Seeded on first use so edits survive navigation but not a reload. */
let profiles: ISignatureProfile[] | null = null;
let templates: ISignatureTemplate[] | null = null;

function store() {
  if (!profiles) profiles = seedProfiles();
  if (!templates) templates = seedTemplates();
  return { profiles, templates };
}

/**
 * Point the fixtures at whoever is signed in.
 *
 * **The name comes from BC_Identity, not from the token.** The employee B2C tokens here carry no
 * `name` claim — which is exactly why an earlier version of this resolved the mailbox address
 * correctly and left the name showing a placeholder person. `UserService` holds the record the
 * rest of the app renders (it is what the HR sidebar shows), so that is the honest source; the
 * MSAL account is the fallback for a session where it has not loaded.
 *
 * Everything the app cannot prove — department, desk line, address, the regulatory wording — stays
 * invented, because in production those come from the HR record and the entity defaults, and the
 * fixtures have neither.
 *
 * Retried on every request until a real name lands rather than latching on the first attempt:
 * `currentUser$` is a BehaviorSubject that starts empty, and `loadProfiles()` fires before the
 * component's own `getCurrentUser()` resolves. Latching early is what would pin the session to the
 * placeholder.
 */
let identityResolved = false;

function applySignedInIdentity(users: UserService | null, msal: MsalService | null): void {
  if (identityResolved) return;

  const identity = resolveIdentity(users, msal);
  if (!identity) return;

  identityResolved = applyFixtureIdentity(identity);

  // Re-seed, because the placeholder is already baked into anything seeded before the name
  // arrived. Only ever happens in the first moments of a session.
  if (identityResolved) profiles = null;
}

function resolveIdentity(users: UserService | null, msal: MsalService | null): FixtureIdentity | null {
  const user = readCachedUser(users);
  const fromIdentity = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  if (fromIdentity) {
    return {
      fullName: fromIdentity,
      preferredName: user?.firstName?.trim(),
      email: user?.email?.trim(),
      // Present only when the caller asked for details; My Signature does not, so this is usually
      // absent and the seeded job title stands.
      jobTitle: user?.userInfo?.jobDescription?.trim(),
    };
  }

  const account = msal?.instance?.getActiveAccount() ?? msal?.instance?.getAllAccounts()?.[0];
  if (!account) return null;

  const name = account.name?.trim();
  return {
    fullName: name,
    // "Alex Georgiou" -> "Alex". Wrong for anyone whose given name is not first, which is
    // why the real field is user-editable rather than derived.
    preferredName: name?.split(/\s+/)[0],
    email: account.username?.trim(),
  };
}

/** `currentUser$` is a BehaviorSubject, so subscribing reads its current value synchronously. */
function readCachedUser(users: UserService | null): UserVM | null {
  if (!users) return null;

  let value: UserVM | null = null;
  try {
    const sub = users.getCurrentUserAsObs().subscribe(v => (value = v));
    sub.unsubscribe();
  } catch {
    return null;
  }

  // Seeded as `{} as UserVM` until the first load resolves.
  return value?.firstName || value?.email ? value : null;
}

function ok<T>(body: T): Observable<HttpEvent<T>> {
  return of(new HttpResponse({ status: 200, body })).pipe(delay(LATENCY_MS));
}

function fail(status: number, statusText: string, error: unknown): Observable<never> {
  return throwError(() => new HttpErrorResponse({ status, statusText, error }));
}

/** A fresh optimistic-concurrency token, so a second editor with a stale copy loses on PUT. */
function nextRowVersion(): string {
  return btoa(String(Date.now()));
}

function pickTemplate(
  all: ISignatureTemplate[],
  profile: ISignatureProfile,
  variant: PreviewVariant,
): ISignatureTemplate | undefined {
  if (variant === 'primary') {
    return all.find(t => t.id === profile.templateId);
  }
  const wanted = variant === 'reply' ? TemplateVariant.Reply : TemplateVariant.Forward;
  return all.find(t => t.legalEntityId === profile.legalEntityId && t.variant === wanted && t.isPublished)
    ?? all.find(t => t.id === profile.templateId);
}

/**
 * The values a template reads that are not the profile's own fields: entity name, the logo and
 * sign-off images, and one ready-made icon URL per social platform.
 *
 * Mirrors `SignatureService.BuildRenderValues` / `AddSocialIconUrls` on the server. The icon URL
 * is composed here and guarded on in the template, so a firm with no icon folder configured
 * renders no icons rather than `src="/linkedIn.png"` — which would resolve against the recipient's
 * mail host and 404.
 */
function renderExtras(profile: ISignatureProfile): Record<string, string | null> {
  const branding = FIXTURE_ENTITY_BRANDING[profile.legalEntityId] ?? {};
  const iconBase = (branding['socialIconBaseUrl'] ?? '').replace(/\/+$/, '');
  const valueOf = (key: string) => profile.fields.find(f => f.key === key)?.value ?? null;

  const extras: Record<string, string | null> = {
    legalEntityName: profile.legalEntityName,
    logoUrl: branding['logoUrl'] ?? null,
    signOffImageUrl: branding['signOffImageUrl'] ?? null,
  };

  for (const key of ['linkedIn', 'x', 'facebook', 'instagram', 'youtube', 'whatsapp', 'telegram']) {
    extras[`${key}Icon`] = iconBase && valueOf(key) ? `${iconBase}/${key}.png` : null;
  }

  return extras;
}

function previewProfile(profile: ISignatureProfile, variant: PreviewVariant) {
  const template = pickTemplate(store().templates, profile, variant);
  if (!template) return { html: '', plainText: '' };
  return renderSignature({
    bodyHtml: template.bodyHtml,
    bodyPlainText: template.bodyPlainText,
    fields: profile.fields,
    profileId: profile.id,
    templateVersion: template.version,
    extras: renderExtras(profile),
  });
}

function summarise(profile: ISignatureProfile) {
  return {
    id: profile.id,
    name: profile.name,
    legalEntityId: profile.legalEntityId,
    legalEntityName: profile.legalEntityName,
    status: profile.status,
    isDefault: profile.isDefault,
    templateId: profile.templateId,
    templateName: profile.templateName,
  };
}

function summariseTemplate(template: ISignatureTemplate) {
  const { id, familyId, legalEntityId, legalEntityName, name, version, kind, variant, isPublished, publishedAtUtc } = template;
  return { id, familyId, legalEntityId, legalEntityName, name, version, kind, variant, isPublished, publishedAtUtc };
}

// ── Self-service ──────────────────────────────────────────────────────────────────────────────

function handleMe(
  method: string,
  segments: string[],
  req: HttpRequest<unknown>,
): Observable<HttpEvent<unknown>> | null {
  const { profiles: all } = store();

  if (segments[1] !== 'profiles') return null;

  // /me/profiles
  if (method === 'GET' && segments.length === 2) {
    return ok(all.map(summarise));
  }

  const profile = all.find(p => p.id === segments[2]);
  if (!profile) return fail(404, 'Not Found', { message: 'No such profile.' });

  const action = segments[3];

  if (method === 'GET' && !action) return ok(profile);

  if (method === 'GET' && action === 'preview') {
    const variant = (req.params.get('variant') ?? 'primary') as PreviewVariant;
    return ok(previewProfile(profile, variant));
  }

  if (method === 'GET' && action === 'export') {
    const format = req.params.get('format') ?? 'html';
    const rendered = previewProfile(profile, 'primary');
    const body = format === 'txt' ? rendered.plainText : rendered.html;
    const type = format === 'txt' ? 'text/plain;charset=utf-8' : 'text/html;charset=utf-8';
    return of(new HttpResponse({ status: 200, body: new Blob([body], { type }) })).pipe(delay(LATENCY_MS));
  }

  if (method === 'PUT' && !action) {
    const body = req.body as IUpdateProfileRequest;

    // Stale editor. The real API compares the rowversion column; losing cleanly is the point.
    if (body.rowVersion !== profile.rowVersion) {
      return fail(409, 'Conflict', { message: 'This signature was changed elsewhere. Reload and try again.' });
    }

    // A Locked key in the payload fails the whole request rather than being dropped. That is what
    // buildUpdatePayload exists to prevent, so the fixture enforces it — otherwise a regression in
    // the filter would pass unnoticed until the real API started rejecting saves.
    const locked = body.fields.filter(f =>
      profile.fields.find(existing => existing.key === f.key)?.editability !== FieldEditability.UserEditable);
    if (locked.length) {
      return fail(400, 'Bad Request', {
        message: `Fields cannot be edited here: ${locked.map(f => f.key).join(', ')}.`,
      });
    }

    for (const change of body.fields) {
      const field = profile.fields.find(f => f.key === change.key);
      if (field) field.value = change.value;
    }
    profile.rowVersion = nextRowVersion();
    return ok(profile);
  }

  // Nothing is uploaded anywhere in demo mode, and the answer says so rather than pretending a
  // publish happened.
  if (method === 'POST' && action === 'publish-assets') {
    return ok({
      logoUrl: null,
      socialIconBaseUrl: '/assets/signature-icons',
      signOffImageUrl: null,
      notes: ['Demo data: nothing was published while the fixtures are answering.'],
    });
  }

  if (method === 'POST' && action === 'set-default') {
    all.forEach(p => (p.isDefault = p.id === profile.id));
    return ok(null);
  }

  if (method === 'POST' && action === 'submit') {
    profile.status = SignatureProfileStatus.PendingApproval;
    profile.rowVersion = nextRowVersion();
    return ok(profile);
  }

  return null;
}

// ── Admin ─────────────────────────────────────────────────────────────────────────────────────

function handleAdmin(
  method: string,
  segments: string[],
  req: HttpRequest<unknown>,
): Observable<HttpEvent<unknown>> | null {
  const { profiles: allProfiles, templates: all } = store();

  if (segments[1] !== 'templates') return null;

  // /admin/templates
  if (method === 'GET' && segments.length === 2) {
    const entityId = req.params.get('entityId');
    const rows = entityId ? all.filter(t => t.legalEntityId === Number(entityId)) : all;
    return ok(rows.map(summariseTemplate));
  }

  if (method === 'POST' && segments.length === 2) {
    const body = req.body as ISaveTemplateRequest;
    const id = `t-new-${all.length + 1}`;
    const family = body.familyId ? all.find(t => t.familyId === body.familyId) : undefined;
    const created: ISignatureTemplate = {
      id,
      familyId: family?.familyId ?? id,
      legalEntityId: body.legalEntityId,
      legalEntityName: all.find(t => t.legalEntityId === body.legalEntityId)?.legalEntityName ?? 'BCT',
      name: body.name,
      // Next version of the family, or 1 for a new template — as the API numbers them.
      version: family
        ? Math.max(...all.filter(t => t.familyId === family.familyId).map(t => t.version)) + 1
        : 1,
      kind: body.kind,
      variant: body.variant,
      bodyHtml: body.bodyHtml,
      bodyPlainText: body.bodyPlainText,
      requiresApproval: body.requiresApproval ?? family?.requiresApproval ?? true,
      isPublished: false,
      publishedAtUtc: null,
      supersededByTemplateId: null,
      policies: body.policies,
    };
    all.push(created);
    return ok(created);
  }

  // /admin/templates/preview — matched before the :id routes, or a template called "preview" wins.
  if (method === 'POST' && segments[2] === 'preview') {
    const body = req.body as ISaveTemplateRequest;
    const sample = allProfiles.find(p => p.legalEntityId === body.legalEntityId) ?? allProfiles[0];
    return ok(renderSignature({
      bodyHtml: body.bodyHtml,
      bodyPlainText: body.bodyPlainText,
      fields: sample.fields,
      profileId: 'preview',
      templateVersion: 0,
    }));
  }

  const template = all.find(t => t.id === segments[2]);
  if (!template) return fail(404, 'Not Found', { message: 'No such template.' });

  const action = segments[3];

  if (method === 'GET' && !action) return ok(template);

  if (method === 'PUT' && !action) {
    // A published version is immutable. The studio disables save, but the API is the enforcement
    // point, so the fixture refuses too.
    if (template.isPublished) {
      return fail(409, 'Conflict', { message: 'Published templates cannot be edited. Create a new version.' });
    }
    const body = req.body as ISaveTemplateRequest;
    Object.assign(template, {
      name: body.name,
      bodyHtml: body.bodyHtml,
      bodyPlainText: body.bodyPlainText,
      policies: body.policies,
      variant: body.variant,
      kind: body.kind,
    });
    return ok(template);
  }

  if (method === 'GET' && action === 'publish') {
    return ok({
      affectedProfiles: FIXTURE_PROFILE_COUNTS[template.legalEntityId] ?? 0,
      conflicts: [],
    });
  }

  if (method === 'POST' && action === 'publish') {
    const confirmation = (req.body as { confirmation?: string })?.confirmation;
    if (confirmation !== template.name) {
      return fail(409, 'Conflict', {
        affectedProfiles: FIXTURE_PROFILE_COUNTS[template.legalEntityId] ?? 0,
        conflicts: [],
        message: 'The confirmation did not match the template name.',
      });
    }
    // Publishing supersedes the live version of the same template (its family) and nothing else.
    all.filter(t => t.id !== template.id
      && t.familyId === template.familyId
      && t.isPublished)
      .forEach(t => {
        t.isPublished = false;
        t.supersededByTemplateId = template.id;
      });
    template.isPublished = true;
    template.publishedAtUtc = new Date().toISOString();
    return ok(template);
  }

  return null;
}

/**
 * One in-flight request for the signed-in user, shared by every fixtured call that arrives before
 * it resolves.
 *
 * Without this the module's first request answers before `getCurrentUser()` has returned, and the
 * form paints the placeholder name while the preview — rendered a beat later — shows the real one.
 * Two different people on one screen is worse than either.
 */
let identityRequest: Observable<void> | null = null;

function ensureIdentity(users: UserService | null, msal: MsalService | null): Observable<void> {
  applySignedInIdentity(users, msal);
  if (identityResolved || !users) return of(void 0);

  identityRequest ??= users.getCurrentUser(false).pipe(
    catchError(() => of(null)),
    tap(() => applySignedInIdentity(users, msal)),
    map(() => void 0),
    shareReplay(1),
  );

  return identityRequest;
}

export function signatureFixturesInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (!environment.useSignatureFixtures) return next(req);

  const base = `${environment.apiConfig.corporateAPI.endpoint}/api/v1/signatures`;
  // Checked before anything else so the identity lookup below — which is itself an HTTP call
  // through this same chain — passes straight through instead of recursing.
  if (!req.url.startsWith(base)) return next(req);

  // Functional interceptors run inside an injection context, so both are reachable here without
  // threading a service through every fixture function. Optional and guarded: a fixture must never
  // be the reason a request fails.
  let identity$: Observable<void>;
  try {
    identity$ = ensureIdentity(
      inject(UserService, { optional: true }),
      inject(MsalService, { optional: true }),
    );
  } catch {
    identity$ = of(void 0);   // Identity unavailable; the seeded placeholder stands.
  }

  return identity$.pipe(switchMap(() => {
    const path = req.url.slice(base.length).split('?')[0];
    const segments = path.split('/').filter(Boolean);
    const method = req.method.toUpperCase();

    const handled = segments[0] === 'me'
      ? handleMe(method, segments, req)
      : segments[0] === 'admin'
        ? handleAdmin(method, segments, req)
        : null;

    // Anything the fixtures do not implement is a 501 rather than a pass-through: letting it reach
    // a host that is not deployed produces a DNS error that reads like a bug in the screen.
    return handled ?? fail(501, 'Not Implemented', {
      message: `No signature fixture for ${method} ${path}. See signature-fixtures.interceptor.ts.`,
    });
  }));
}
